import type { ApiErrorCode } from "@cs2/contracts/transport";

/**
 * Ein Serverfehler als Klasse statt als anonym erweitertes `Error`.
 *
 * Vorher warf `request()` ein `Object.assign(new Error(), { status, details })`,
 * und drei voneinander unabhängige Stellen leiteten daraus wieder Fakten ab:
 * `http-status.ts` per Duck-Typing auf `.status`, `editor/describe-error.ts`
 * auf `.details`, dazu mindestens dreimal ein inline
 * `error instanceof Error ? … : …`. Keine davon kannte `code`.
 *
 * `code` ist die verlässlichere Unterscheidung als der Statuscode: 409 kann
 * Versionskonflikt, blockierte Utility oder paralleles Veröffentlichen
 * bedeuten. Das Vokabular teilt sich mit dem Server (`apiErrorCodeSchema`) —
 * ein Code, den nur eine Seite kennt, ist ein Typfehler.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    /**
     * Bewusst `string`, nicht `ApiErrorCode`: das ist ein Wert vom Draht. Eine
     * ältere oder neuere Serverfassung darf einen Code schicken, den dieser
     * Client nicht kennt, ohne dass die Antwort unlesbar wird. Für den
     * typgeprüften Vergleich gibt es `is()`.
     */
    readonly code: string,
    message: string,
    /** Begründungen einer Ablehnung, für die Anzeige. */
    readonly details?: readonly string[],
    /** Serverseitiger Stand bei einem Versionskonflikt. */
    readonly current?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }

  is(code: ApiErrorCode): boolean {
    return this.code === code;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** Statuscode eines Fehlers, sofern es einer vom Server ist. */
export function statusOf(error: unknown): number | undefined {
  return isApiError(error) ? error.status : undefined;
}

/** Fehlercode eines Fehlers, sofern es einer vom Server ist. */
export function codeOf(error: unknown): string | undefined {
  return isApiError(error) ? error.code : undefined;
}
